/**
 * KCIS Content Studio — the one owner-facing page for every learning
 * experience.
 *
 * Route: /kcis/admin/content-studio
 *
 * One intake. Five product types (University Course, CE Course,
 * Certification Module, Workshop, Coaching Program). One click to
 * build. Preview, edit, request changes, assign, publish, archive
 * or remove all from the same page.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("kcis_admin_token") || "";
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function apiGet(path) {
  const r = await fetch(`${API}${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error(`GET ${path} -> ${r.status}`);
  return r.json();
}

async function apiPost(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body || {}),
  });
  const text = await r.text();
  let data;
  try { data = JSON.parse(text); } catch { data = { raw: text }; }
  if (!r.ok) {
    const msg = data?.detail?.message || data?.detail?.code || `POST ${path} -> ${r.status}`;
    const err = new Error(msg);
    err.data = data;
    throw err;
  }
  return data;
}

async function apiDelete(path) {
  const r = await fetch(`${API}${path}`, {
    method: "DELETE", headers: authHeaders(),
  });
  const text = await r.text();
  let data;
  try { data = JSON.parse(text); } catch { data = { raw: text }; }
  if (!r.ok) {
    const err = new Error(data?.detail?.message || `DELETE ${path} -> ${r.status}`);
    err.data = data;
    throw err;
  }
  return data;
}

async function openPreview(courseId) {
  try {
    const r = await fetch(
      `${API}/api/kcis/admin/kape/courses/${courseId}/preview-token`,
      { method: "POST", headers: authHeaders() });
    if (!r.ok) throw new Error(`preview token ${r.status}`);
    const data = await r.json();
    window.open(`/kape/preview?token=${encodeURIComponent(data.token)}`, "_blank");
  } catch (e) {
    alert(`Could not open preview: ${e.message || e}`);
  }
}

const STATUS_LABEL = {
  building:  "Building",
  ready:     "Ready for review",
  published: "Published",
  archived:  "Archived",
};

const STATUS_TABS = [
  { key: "all",       label: "All active" },
  { key: "building",  label: "Building" },
  { key: "ready",     label: "Ready for review" },
  { key: "published", label: "Published" },
  { key: "archived",  label: "Archived" },
];

export default function ContentStudio() {
  const nav = useNavigate();
  const [view, setView] = useState("home");   // home | create | detail
  const [rows, setRows] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [tab, setTab] = useState("all");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeCourseId, setActiveCourseId] = useState("");

  const refresh = async () => {
    setLoading(true); setError("");
    try {
      const data = await apiGet("/api/kcis/admin/content-studio/list?include_archived=true");
      setRows(data.items || []);
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await apiGet("/api/kcis/admin/content-studio/config");
        if (alive) setProductTypes(cfg.product_types || []);
      } catch { /* fall back to hard-coded on the create screen */ }
    })();
    refresh();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (typeFilter && r.product_type !== typeFilter) return false;
    if (tab === "all") return !r.archived;
    if (tab === "archived") return !!r.archived;
    return r.row_status === tab && !r.archived;
  }), [rows, tab, typeFilter]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 p-6" data-testid="content-studio-page">
      <div className="max-w-6xl mx-auto">
        <Header
          onCreate={() => setView("create")}
          onHome={() => { setView("home"); setActiveCourseId(""); }}
          view={view}
        />

        {view === "create" && (
          <CreateForm
            productTypes={productTypes}
            onCancel={() => setView("home")}
            onCreated={async (courseId) => {
              setActiveCourseId(courseId);
              setView("detail");
              await refresh();
            }}
          />
        )}

        {view === "detail" && activeCourseId && (
          <CourseDetail
            courseId={activeCourseId}
            onBack={() => { setView("home"); setActiveCourseId(""); refresh(); }}
            onOpenEditor={(cid) => nav(`/kcis/admin/kape/course-editor/${cid}`)}
            onPreview={openPreview}
          />
        )}

        {view === "home" && (
          <>
            <Filters productTypes={productTypes}
                     tab={tab} setTab={setTab}
                     typeFilter={typeFilter} setTypeFilter={setTypeFilter} />
            {error && <div className="mb-3 text-sm text-red-600" data-testid="studio-error">{error}</div>}
            {loading && <div className="text-sm text-slate-500">Loading…</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map((r) => (
                <CourseCard key={r.id} row={r}
                             onOpen={() => { setActiveCourseId(r.id); setView("detail"); }}
                             onPreview={() => openPreview(r.id)} />
              ))}
              {!loading && filtered.length === 0 && (
                <div className="col-span-2 text-sm text-slate-500 text-center py-10 bg-white rounded-xl border"
                     data-testid="studio-empty">
                  Nothing in this view yet. Click <b>+ Create New</b> to build a
                  learning experience.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════ Header ═══════════════════════

function Header({ onCreate, onHome, view }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-purple-700 font-semibold">
          KCIS Content Studio
        </div>
        <h1 className="text-3xl font-semibold mt-1">My Content</h1>
        <p className="text-sm text-slate-500 mt-1">
          Create and manage every learning experience from one place.
        </p>
      </div>
      <div className="flex gap-2">
        {view !== "home" && (
          <button onClick={onHome}
                  className="px-4 py-2 text-sm border rounded-lg"
                  data-testid="studio-back">
            ← Back to My Content
          </button>
        )}
        {view === "home" && (
          <button className="px-4 py-2 text-sm bg-purple-700 text-white rounded-lg font-medium"
                  onClick={onCreate}
                  data-testid="studio-create-new-btn">
            + Create New
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════ Filters ═══════════════════════

function Filters({ productTypes, tab, setTab, typeFilter, setTypeFilter }) {
  return (
    <div className="mb-4 space-y-3" data-testid="studio-filters">
      <div className="flex gap-2 flex-wrap" data-testid="studio-status-tabs">
        {STATUS_TABS.map((t) => (
          <button key={t.key}
                  className={`px-3 py-1.5 text-xs rounded-full border ${tab===t.key?"bg-purple-700 text-white border-purple-700":"bg-white text-slate-700 border-slate-300"}`}
                  onClick={() => setTab(t.key)}
                  data-testid={`studio-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap items-center text-xs" data-testid="studio-type-filters">
        <span className="text-slate-500">Type:</span>
        <button className={`px-2.5 py-1 rounded-full border ${!typeFilter?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-300"}`}
                onClick={() => setTypeFilter("")}
                data-testid="studio-type-filter-all">
          All
        </button>
        {productTypes.map((pt) => (
          <button key={pt.value}
                  className={`px-2.5 py-1 rounded-full border ${typeFilter===pt.value?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-300"}`}
                  onClick={() => setTypeFilter(pt.value)}
                  data-testid={`studio-type-filter-${pt.value}`}>
            {pt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════ Course card (My Content grid) ═══════════════════════

function CourseCard({ row, onOpen, onPreview }) {
  const status = row.row_status || "building";
  return (
    <div className="bg-white border rounded-xl p-4 shadow-sm"
         data-testid={`studio-card-${row.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-purple-700 font-semibold"
               data-testid={`studio-card-type-${row.id}`}>
            {(row.product_type || "content").replace(/_/g, " ")}
          </div>
          <div className="text-lg font-semibold mt-0.5 truncate">{row.title}</div>
          <div className="text-xs text-slate-500 mt-1">
            {row.credit_hours ? `${row.credit_hours} · ` : ""}
            {row.modules} modules · {row.lessons} lessons
          </div>
          <div className="text-xs mt-1 flex gap-2 items-center flex-wrap">
            <span className={`inline-block px-2 py-0.5 rounded-full ${status==="published"?"bg-emerald-100 text-emerald-800":status==="ready"?"bg-blue-100 text-blue-800":status==="archived"?"bg-slate-200 text-slate-600":"bg-amber-100 text-amber-800"}`}>
              {STATUS_LABEL[status] || status}
            </span>
            {row.catalog_section && (
              <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                {row.catalog_section.replace(/_/g, " ")}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-4 flex-wrap">
        <button onClick={onPreview}
                className="text-xs px-3 py-1.5 border rounded-lg"
                data-testid={`studio-card-preview-${row.id}`}>
          Preview
        </button>
        <button onClick={onOpen}
                className="text-xs px-3 py-1.5 bg-purple-700 text-white rounded-lg"
                data-testid={`studio-card-open-${row.id}`}>
          Open
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════ Create form ═══════════════════════

const FALLBACK_PRODUCT_TYPES = [
  {value:"university_course", label:"University Course",
   duration_field:"credit_hours", duration_label:"Credit hours (1–5)",
   duration_min:1, duration_max:5, duration_step:1,
   destination:"University course catalog"},
  {value:"continuing_education", label:"Continuing-Education Course",
   duration_field:"credit_hours", duration_label:"Credit hours (1–5)",
   duration_min:1, duration_max:5, duration_step:1,
   destination:"Continuing-Education catalog"},
  {value:"certification_module", label:"Certification Module",
   duration_field:"credit_hours", duration_label:"Credit hours (1–5)",
   duration_min:1, duration_max:5, duration_step:1,
   destination:"Certification programs"},
  {value:"workshop", label:"Workshop",
   duration_field:"duration_hours", duration_label:"Expected duration (hours)",
   duration_min:1, duration_max:40, duration_step:1,
   destination:"Workshops catalog"},
  {value:"coaching_program", label:"Coaching Program",
   duration_field:"weeks_or_sessions", duration_label:"Weeks or sessions",
   duration_min:1, duration_max:52, duration_step:1,
   destination:"Coaching Programs"},
];

const EXAMPLE_PROMPT = "Create TRI-101 Introduction to Trichology for beginning professional trichology students. This is a 3-credit-hour introductory college-level course. Teach the foundations of trichology, hair and scalp anatomy, hair growth cycles, common hair and scalp conditions, client consultation, scope of practice, and introductory clinical reasoning. Create an interactive lecture/presentation with case studies and knowledge checks, a student workbook, a 15-question quiz with answers and rationales, and one assignment with a grading rubric. Students should be able to explain core trichology concepts and apply them to basic client scenarios by the end of the course.";

const GUIDED_TEMPLATE = "Create [COURSE TITLE/CODE] for [TARGET LEARNER]. This is a [ACADEMIC LEVEL / CREDIT HOURS / LENGTH] course. Students should learn [MAIN TOPICS]. By the end of the course, students should be able to [LEARNER OUTCOMES]. Create an interactive lecture/presentation, workbook, quiz, and assignment. Include [CASE STUDIES / ACTIVITIES / PRACTICAL EXERCISES / OTHER REQUIREMENTS].";

const HELPFUL_DETAILS = [
  "Course title or course code",
  "Who the course is for",
  "Academic level",
  "Credit hours or approximate length",
  "Main topics students should learn",
  "What students should be able to do by the end",
  "Desired number of sections/slides if known",
  "Quiz length",
  "Assignment type",
  "Special activities, case studies, or practical exercises",
  "Source materials you want KCIS to use",
];

function CreateForm({ productTypes, onCancel, onCreated }) {
  const types = productTypes.length ? productTypes : FALLBACK_PRODUCT_TYPES;
  const [productType, setProductType] = useState(types[0].value);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Advanced options — kept collapsed by default per §2/§30.
  const [title, setTitle] = useState("");
  const [instructor, setInstructor] = useState("");
  const [duration, setDuration] = useState(3);
  const [source, setSource] = useState("");
  const [moduleCount, setModuleCount] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [targetLearner, setTargetLearner] = useState("");

  const pt = types.find((p) => p.value === productType) || FALLBACK_PRODUCT_TYPES[0];

  // Derive a working title from the first line of the prompt when the
  // owner did not provide one explicitly. Keeps the intake to
  // a *single* required field.
  const derivedTitle = () => {
    if (title.trim().length >= 3) return title.trim();
    const first = (prompt.split(/\n|\.|:/)[0] || "").trim();
    // Strip leading "Create" verbs so we don't set the title to
    // "Create TRI-101 Introduction to Trichology". Prefer just
    // "TRI-101 Introduction to Trichology".
    return first.replace(/^\s*(please\s+)?(create|design|build|generate|make)\s+/i, "").slice(0, 180) || "Untitled course";
  };

  const submit = async (mode) => {
    if (busy) return;   // idempotency guard: prevent duplicate submissions
    if (prompt.trim().length < 15) {
      setError("Please describe the course in a sentence or two.");
      return;
    }
    const durNum = Number(duration);
    if (!Number.isFinite(durNum) || durNum < (pt.duration_min || 1)) {
      setError(`${pt.duration_label || "Duration"} required.`); return;
    }
    setBusy(true); setError("");
    try {
      const payload = {
        product_type: productType,
        title: derivedTitle(),
        learner_outcome: prompt.trim(),
        primary_instructor_id: instructor.trim(),
        source_information: source.trim(),
        mode,
        module_count_hint: moduleCount ? Number(moduleCount) : null,
        difficulty_hint: difficulty || "",
        target_learner: targetLearner.trim(),
      };
      payload[pt.duration_field || "credit_hours"] = durNum;

      // Full-course generation runs asynchronously on the backend so
      // the browser is not held on the same HTTP request while OpenAI
      // is generating (the preview ingress caps requests at ~60 s).
      // The POST returns a generation_id in ~1 s; we then poll a tiny
      // status endpoint every 4 s until it reaches `completed` or
      // `failed`. Polling never triggers another OpenAI request.
      if (mode === "full_course") {
        const started = await apiPost(
          "/api/kcis/admin/content-studio/build", payload);
        const genId = started?.generation_id;
        if (!genId) throw new Error("Course generation did not start. Please try again.");

        // Poll status. Cap at ~10 min to protect against runaway.
        const startedAt = Date.now();
        const MAX_MS = 10 * 60 * 1000;
        for (;;) {
          if (Date.now() - startedAt > MAX_MS) {
            throw new Error("Course generation is taking longer than expected. Please check My Content shortly.");
          }
          await new Promise((res) => setTimeout(res, 4000));
          let st;
          try {
            st = await apiGet(
              `/api/kcis/admin/content-studio/generations/${genId}`);
          } catch (pollErr) {
            // Transient network hiccup — keep polling.
            continue;
          }
          if (st.status === "completed") {
            onCreated(st.course_id);
            return;
          }
          if (st.status === "failed") {
            throw new Error(st.message || "Course generation failed.");
          }
          // status === "generating" | "queued" → keep waiting.
        }
      }

      const res = await apiPost("/api/kcis/admin/content-studio/build", payload);
      onCreated(res.course_id);
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border rounded-xl p-6 shadow-sm" data-testid="studio-create-form">
      <h2 className="text-xl font-semibold mb-1">Create a course from a prompt</h2>
      <p className="text-sm text-slate-500 mb-5">
        Describe the course you want. KCIS generates the complete
        course — modules, lessons, workbook, quiz, assignments — and
        keeps every piece editable.
      </p>

      {/* Experience type — 5 pill buttons across the top */}
      <div>
        <label className="text-xs uppercase tracking-widest text-purple-700 font-semibold">
          Experience type
        </label>
        <div className="flex gap-2 mt-2 flex-wrap" data-testid="studio-form-experience-type-row">
          {types.map((p) => (
            <button key={p.value}
                    onClick={() => setProductType(p.value)}
                    data-testid={`studio-form-exp-${p.value}`}
                    className={`px-3 py-1.5 text-xs rounded-full border ${productType===p.value?"bg-purple-700 text-white border-purple-700":"bg-white text-slate-700 border-slate-300"}`}>
              {p.label}
            </button>
          ))}
        </div>
        {pt.destination && (
          <div className="text-[11px] text-slate-500 mt-2">
            Publishes into: <b>{pt.destination}</b>
          </div>
        )}
      </div>

      {/* The one required field — the prompt */}
      <div className="mt-5">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <label className="text-xs uppercase tracking-widest text-purple-700 font-semibold">
            Describe your course
          </label>
          <div className="flex gap-2">
            <button type="button"
                    onClick={() => setPrompt(EXAMPLE_PROMPT)}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="studio-form-use-example-btn">
              Use Example
            </button>
            <button type="button"
                    onClick={() => setPrompt(GUIDED_TEMPLATE)}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="studio-form-use-template-btn">
              Use Guided Template
            </button>
          </div>
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="w-full mt-2 border rounded-lg px-3 py-2 h-40 text-sm leading-relaxed"
          placeholder="Describe the course you want KCIS to create."
          data-testid="studio-form-prompt" />

        {/* Helpful details panel — non-prescriptive, no wizard */}
        <div className="mt-2 rounded-lg bg-purple-50/60 border border-purple-100 px-3 py-2"
             data-testid="studio-form-helpful-details">
          <div className="text-[11px] uppercase tracking-widest text-purple-700 font-semibold">
            Helpful details to include
          </div>
          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-700">
            {HELPFUL_DETAILS.map((d) => (
              <li key={d} className="before:content-['•'] before:text-purple-500 before:mr-1">
                {d}
              </li>
            ))}
          </ul>
          <div className="text-[11px] text-slate-500 mt-1">
            None of these are required. Just type a normal prompt — KCIS will do the rest.
          </div>
        </div>
      </div>

      {/* Advanced options — collapsed by default */}
      <div className="mt-4 border-t pt-3">
        <button type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                data-testid="studio-form-advanced-toggle"
                className="text-xs text-slate-600 underline decoration-dotted">
          {showAdvanced ? "▾ Hide advanced options" : "▸ Advanced options"}
        </button>
        {showAdvanced && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3"
                data-testid="studio-form-advanced-panel">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500">Working title (optional)</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                     className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                     data-testid="studio-form-title"
                     placeholder="Derived from your prompt if left blank" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500">Instructor slug (optional)</label>
              <input value={instructor} onChange={(e) => setInstructor(e.target.value)}
                     className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                     data-testid="studio-form-instructor"
                     placeholder="dr-christy-vonta" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500">
                {pt.duration_label || "Duration"}
              </label>
              <input type="number"
                     min={pt.duration_min || 1}
                     max={pt.duration_max || 100}
                     value={duration}
                     onChange={(e) => setDuration(e.target.value)}
                     className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                     data-testid="studio-form-duration" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500">Desired module count</label>
              <input type="number" min="1" max="24"
                     value={moduleCount}
                     onChange={(e) => setModuleCount(e.target.value)}
                     className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                     placeholder="Auto"
                     data-testid="studio-form-module-count" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500">Difficulty</label>
              <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}
                        className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-white"
                        data-testid="studio-form-difficulty">
                <option value="">Auto</option>
                <option value="introductory">Introductory</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500">Target learner</label>
              <input value={targetLearner} onChange={(e) => setTargetLearner(e.target.value)}
                     className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                     data-testid="studio-form-target-learner"
                     placeholder="e.g., new practitioners" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[11px] uppercase tracking-widest text-slate-500">Source material / notes</label>
              <textarea value={source} onChange={(e) => setSource(e.target.value)}
                        className="w-full mt-1 border rounded-lg px-3 py-2 text-sm h-20"
                        data-testid="studio-form-source" />
            </div>
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-600 mt-3" data-testid="studio-form-error">{error}</div>}

      {/* Two big buttons — per §3 */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <button onClick={() => submit("full_course")} disabled={busy}
                className="px-6 py-3 rounded-lg bg-purple-700 hover:bg-purple-800 text-white font-medium disabled:opacity-60 flex-1"
                data-testid="studio-form-build-full-btn">
          {busy ? "Generating course…" : "Generate Course"}
        </button>
        <button onClick={() => submit("blueprint_only")} disabled={busy}
                className="px-6 py-3 rounded-lg border-2 border-purple-700 text-purple-800 font-medium disabled:opacity-60 flex-1"
                data-testid="studio-form-blueprint-btn">
          {busy ? "Working…" : "Generate Blueprint only"}
        </button>
        <button onClick={onCancel} disabled={busy}
                className="px-4 py-3 text-sm text-slate-500"
                data-testid="studio-form-cancel-btn">
          Cancel
        </button>
      </div>
      <div className="text-[11px] text-slate-400 mt-2">
        Generate Course sends your prompt to OpenAI (KCIS Course Author)
        and produces a complete, editable course in one action. Blueprint
        is optional.
      </div>
    </div>
  );
}

// ═══════════════════════ Detail (Progress + Actions) ═══════════════════════

function CourseDetail({ courseId, onBack, onOpenEditor, onPreview }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [changePreview, setChangePreview] = useState(null);
  const [changePrompt, setChangePrompt] = useState("");
  const [programs, setPrograms] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [showPublish, setShowPublish] = useState(false);
  const [publishPrice, setPublishPrice] = useState("0");
  // Extra Assign fields (curriculum attachment) — per §8.
  const [assignSeq, setAssignSeq] = useState("");
  const [assignRequired, setAssignRequired] = useState(true);
  const [assignPrereq, setAssignPrereq] = useState("");
  const [assignPrice, setAssignPrice] = useState("");

  const tick = async () => {
    try {
      const d = await apiGet(`/api/kcis/admin/content-studio/${courseId}/status`);
      setData(d);
    } catch { /* keep last */ }
  };
  useEffect(() => { tick(); const iv = setInterval(tick, 4000); return () => clearInterval(iv); },
    [courseId]);
  useEffect(() => {
    apiGet("/api/kcis/admin/content-studio/programs")
      .then((r) => setPrograms(r.items || [])).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm text-slate-500 py-6">Loading course…</div>;

  const doAction = async (label, fn) => {
    setBusy(label); setNote("");
    try {
      const res = await fn();
      setNote(res?.message || `${label} — done.`);
      await tick();
    } catch (e) { setNote(`${label} failed: ${e.message || e}`); }
    finally { setBusy(""); }
  };

  const requestChangesPreview = async () => {
    if (changePrompt.trim().length < 3) return;
    try {
      const res = await apiPost(
        `/api/kcis/admin/content-studio/${courseId}/request-changes`,
        { prompt: changePrompt.trim(), confirm: false });
      setChangePreview(res.analysis);
    } catch (e) { setNote(`Preview failed: ${e.message}`); }
  };
  const requestChangesConfirm = async () => {
    try {
      const res = await apiPost(
        `/api/kcis/admin/content-studio/${courseId}/request-changes`,
        { prompt: changePrompt.trim(), confirm: true,
          components: (changePreview?.changed || []).map((c) => c.key) });
      setNote(`Change queued. ${res.applied ? "Approved and manually edited content will be preserved." : ""}`);
      setChangePreview(null); setChangePrompt("");
      await tick();
    } catch (e) { setNote(`Change failed: ${e.message}`); }
  };

  const publish = async () => {
    await doAction("Publish", () => apiPost(
      `/api/kcis/admin/content-studio/${courseId}/publish`,
      { price: Number(publishPrice) || 0, sell: Number(publishPrice) > 0,
        allow_partial: false }));
    setShowPublish(false);
  };

  const assign = async () => {
    await doAction("Assign", () => apiPost(
      `/api/kcis/admin/content-studio/${courseId}/assign`,
      { program_id: selectedProgram,
        sequence: assignSeq ? Number(assignSeq) : null,
        required: assignRequired,
        prerequisite_course_code: assignPrereq.trim(),
        per_course_price_usd: assignPrice ? Number(assignPrice) : null,
      }));
    setShowAssign(false);
  };
  const startFullCourse = () => doAction("Build Full Course", () => apiPost(
    `/api/kcis/admin/content-studio/${courseId}/start-full-course`, {}));

  const archive = async () => {
    if (!window.confirm("Archive this record? Enrollment and payment history will be preserved.")) return;
    await doAction("Archive", () => apiPost(
      `/api/kcis/admin/content-studio/${courseId}/archive`, {}));
  };
  const restore = () => doAction("Restore", () => apiPost(
    `/api/kcis/admin/content-studio/${courseId}/restore`, {}));
  const removeForever = async () => {
    if (!window.confirm("Delete this record permanently? This cannot be undone. Deletion is blocked if any enrollment, payment or grade history exists.")) return;
    await doAction("Delete", () => apiDelete(
      `/api/kcis/admin/content-studio/${courseId}?confirm=true`));
    onBack();
  };
  const retryFailed = () => doAction("Retry", () => apiPost(
    `/api/kcis/admin/content-studio/${courseId}/retry-failed`, {}));

  const p = data.progress || { stages: [], overall_percent: 0, overall_label: "Queued" };
  // Preview is available whenever paid asset automation has run OR
  // when the text-based course is complete (every lesson has body
  // content and at least one slide exists). Missing image/audio must
  // NOT block previewing the completed text course.
  const isReady = data.asset_automation_ran_at
                    || p.overall_label === "Ready for Review"
                    || p.text_ready === true;
  const isPublished = !!data.published_at;
  const isArchived = data.archived;
  const canRetry = p.overall_label === "Failed"
                    || p.overall_label === "Partially Completed";

  return (
    <div className="bg-white border rounded-xl p-6 shadow-sm" data-testid="studio-detail">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-purple-700 font-semibold">
            {(data.product_type || "").replace(/_/g, " ")}
          </div>
          <h2 className="text-2xl font-semibold mt-1 truncate" data-testid="studio-detail-title">
            {data.title || "(untitled)"}
          </h2>
          <div className="text-sm text-slate-500 mt-1">
            {data.credit_hours ? `${data.credit_hours} · ` : ""}
            {data.modules} modules · {data.lessons} lessons ·
            {" "}{data.catalog_section?.replace(/_/g, " ")}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs uppercase tracking-widest text-slate-500">Status</div>
          <div className={`text-lg font-semibold ${p.overall_label === "Failed" ? "text-red-700" : "text-purple-700"}`}
                data-testid="studio-detail-status">
            {p.friendly_label || p.overall_label}
          </div>
          <div className="text-xs text-slate-500">
            {p.overall_percent}% · {p.overall_label}
          </div>
        </div>
      </div>

      {/* Progress stages */}
      <div className="mt-6">
        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-purple-600 transition-all"
                style={{ width: `${p.overall_percent}%` }} />
        </div>
        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          {(p.stages || []).map((s, i) => (
            <li key={s.key}
                className={`flex items-center gap-3 text-sm ${s.done ? "text-slate-700" : s.current ? "text-purple-700 font-medium" : "text-slate-400"}`}
                data-testid={`studio-progress-stage-${i}`}>
              <span className="inline-block w-2 h-2 rounded-full"
                    style={{ background: s.done ? "#7C3AED" : s.current ? "#7C3AED" : "#CBD5E1" }} />
              {s.label}{s.current ? " …" : s.done ? " ✓" : ""}
            </li>
          ))}
        </ul>
        {p.failed_reason && (
          <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
                data-testid="studio-detail-failed">
            <b>Failed:</b> {p.failed_reason}
          </div>
        )}
      </div>

      {/* Primary action row */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="studio-detail-actions">
        {/* Blueprint-only records get the explicit Build Full Course
            button so the two-button model completes in the detail view. */}
        {(data.job?.status === "no_job" || !data.job) && data.lessons > 0 && !isReady && (
          <button onClick={startFullCourse} disabled={!!busy}
                  className="px-4 py-2 text-sm bg-purple-700 text-white rounded-lg"
                  data-testid="studio-detail-build-full-btn">
            Build Full Course
          </button>
        )}
        <button onClick={() => onPreview(courseId)}
                disabled={!isReady}
                className="px-4 py-2 text-sm bg-purple-700 text-white rounded-lg disabled:opacity-40"
                data-testid="studio-detail-preview-btn">
          Preview as Student
        </button>
        <button onClick={() => onOpenEditor(courseId)}
                className="px-4 py-2 text-sm border rounded-lg"
                data-testid="studio-detail-edit-btn">
          Edit Details
        </button>
        <button onClick={() => onOpenEditor(courseId)}
                className="px-4 py-2 text-sm border rounded-lg"
                data-testid="studio-detail-manage-btn">
          Manage Content
        </button>
        <button onClick={() => setShowAssign(true)}
                className="px-4 py-2 text-sm border rounded-lg"
                data-testid="studio-detail-assign-btn">
          Assign to Program
        </button>
        {!isPublished && (
          <button onClick={() => setShowPublish(true)}
                  className="px-4 py-2 text-sm border rounded-lg bg-emerald-50 text-emerald-800 border-emerald-200"
                  data-testid="studio-detail-publish-btn">
            Publish
          </button>
        )}
        {isArchived ? (
          <button onClick={restore} disabled={!!busy}
                  className="px-4 py-2 text-sm border rounded-lg"
                  data-testid="studio-detail-restore-btn">
            Restore
          </button>
        ) : (
          <button onClick={archive} disabled={!!busy}
                  className="px-4 py-2 text-sm border rounded-lg"
                  data-testid="studio-detail-archive-btn">
            Archive
          </button>
        )}
        {!isPublished && !isArchived && (
          <button onClick={removeForever} disabled={!!busy}
                  className="px-4 py-2 text-sm border rounded-lg text-red-700 border-red-200"
                  data-testid="studio-detail-remove-btn">
            Remove
          </button>
        )}
        {p.overall_label === "Failed" || p.overall_label === "Partially Completed" ? (
          <button onClick={retryFailed} disabled={!!busy || !canRetry}
                  className="px-4 py-2 text-sm border rounded-lg bg-amber-50 text-amber-800 border-amber-200"
                  data-testid="studio-detail-retry-btn">
            Retry Failed Components
          </button>
        ) : null}
        <button onClick={onBack}
                className="ml-auto px-4 py-2 text-sm border rounded-lg text-slate-500"
                data-testid="studio-detail-back-btn">
          Close
        </button>
      </div>

      {note && (
        <div className="mt-3 text-sm text-slate-600 bg-slate-50 border rounded-lg p-3"
              data-testid="studio-detail-note">
          {note}
        </div>
      )}

      {/* Request Changes card */}
      <div className="mt-8 border-t pt-6" data-testid="studio-request-changes">
        <h3 className="text-lg font-semibold">Request Changes</h3>
        <p className="text-sm text-slate-500 mt-1">
          Describe what you&apos;d like to change in plain language. You&apos;ll
          see exactly what will change and what stays before anything runs.
        </p>
        <textarea value={changePrompt}
                    onChange={(e) => setChangePrompt(e.target.value)}
                    className="w-full mt-2 border rounded-lg px-3 py-2 h-24"
                    placeholder="e.g. Please rewrite the module 2 workbook with more real-world examples."
                    data-testid="studio-change-prompt" />
        <div className="flex gap-2 mt-2">
          <button onClick={requestChangesPreview}
                  className="px-3 py-1.5 text-xs border rounded-lg"
                  data-testid="studio-change-preview-btn">
            Preview change
          </button>
          {changePreview && (
            <button onClick={requestChangesConfirm}
                    className="px-3 py-1.5 text-xs bg-purple-700 text-white rounded-lg"
                    data-testid="studio-change-confirm-btn">
              Confirm & apply
            </button>
          )}
        </div>
        {changePreview && (
          <div className="mt-3 text-xs" data-testid="studio-change-analysis">
            <div className="mb-1">
              <b>Will change:</b>
              {" "}
              {(changePreview.changed || []).map((c) => c.label).join(", ")}
            </div>
            <div className="mb-1">
              <b>Will stay:</b>
              {" "}
              {(changePreview.preserved || []).map((c) => c.label).join(", ")}
            </div>
            <div>
              <b>New AI/provider usage:</b>{" "}
              {changePreview.needs_provider ? "Yes" : "No"}
            </div>
          </div>
        )}
      </div>

      {/* Publish modal (inline) */}
      {showPublish && (
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4"
              data-testid="studio-publish-panel">
          <div className="text-sm font-semibold text-emerald-900">Publish this experience</div>
          <div className="text-xs text-emerald-800 mt-1">
            It will be added to the {data.catalog_section?.replace(/_/g, " ") || "catalog"}.
          </div>
          <label className="text-xs mt-2 block">Price (USD, 0 = free)</label>
          <input type="number" min="0" step="1"
                 className="w-32 mt-1 border rounded-lg px-2 py-1"
                 value={publishPrice}
                 onChange={(e) => setPublishPrice(e.target.value)}
                 data-testid="studio-publish-price" />
          <div className="mt-3 flex gap-2">
            <button onClick={publish} disabled={!!busy}
                    className="px-3 py-1.5 text-xs bg-emerald-700 text-white rounded-lg"
                    data-testid="studio-publish-confirm-btn">
              Publish
            </button>
            <button onClick={() => setShowPublish(false)}
                    className="px-3 py-1.5 text-xs border rounded-lg">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Assign modal (inline) — curriculum-aware per §8/§20 */}
      {showAssign && (
        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-lg p-4"
              data-testid="studio-assign-panel">
          <div className="text-sm font-semibold">Assign to program curriculum</div>
          <div className="mt-2">
            <label className="text-[11px] uppercase text-slate-500">Program</label>
            <select value={selectedProgram} onChange={(e) => setSelectedProgram(e.target.value)}
                     className="w-full mt-1 border rounded-lg px-2 py-1.5 text-sm bg-white"
                     data-testid="studio-assign-select">
              <option value="">— No program —</option>
              {programs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                  {p.credential ? ` · ${p.credential}` : ""}
                  {(p.course_codes && p.course_codes.length)
                    ? ` (${p.course_codes.length} courses)` : ""}
                </option>
              ))}
            </select>
          </div>
          {selectedProgram && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <label className="text-[11px] uppercase text-slate-500">Sequence (auto)</label>
                <input type="number" min="1" value={assignSeq}
                       onChange={(e) => setAssignSeq(e.target.value)}
                       className="w-full mt-1 border rounded-lg px-2 py-1.5 text-sm"
                       placeholder="Append at end"
                       data-testid="studio-assign-seq" />
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500">Prerequisite (course code)</label>
                <input value={assignPrereq}
                       onChange={(e) => setAssignPrereq(e.target.value)}
                       className="w-full mt-1 border rounded-lg px-2 py-1.5 text-sm"
                       placeholder="e.g., TRI-101"
                       data-testid="studio-assign-prereq" />
              </div>
              <div>
                <label className="text-[11px] uppercase text-slate-500">Per-course price (USD)</label>
                <input type="number" min="0" step="1" value={assignPrice}
                       onChange={(e) => setAssignPrice(e.target.value)}
                       className="w-full mt-1 border rounded-lg px-2 py-1.5 text-sm"
                       placeholder="Optional"
                       data-testid="studio-assign-price" />
              </div>
              <div className="flex items-end gap-2">
                <input type="checkbox" id="assign-required"
                       checked={assignRequired}
                       onChange={(e) => setAssignRequired(e.target.checked)}
                       data-testid="studio-assign-required" />
                <label htmlFor="assign-required" className="text-xs text-slate-700">
                  Required in this program
                </label>
              </div>
            </div>
          )}
          <div className="text-[11px] text-slate-500 mt-2">
            The course is referenced by id in the program&apos;s
            required_courses[] — <b>no duplicate course record is
            created.</b> Only academic types contribute credit hours.
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={assign} disabled={!!busy}
                    className="px-3 py-1.5 text-xs bg-slate-800 text-white rounded-lg"
                    data-testid="studio-assign-confirm-btn">
              Save to curriculum
            </button>
            <button onClick={() => setShowAssign(false)}
                    className="px-3 py-1.5 text-xs border rounded-lg">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
